import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { TileService } from '../../services/tile.service';
import { Tile } from '../../types/tile.types';
import { MAX_NOTE_LENGTH } from './home-widget-constants';

/** Notiz-Kachel: Der Text wird direkt in der Kachel bearbeitet und kurz nach dem Tippen gespeichert. */
@Component({
  selector: 'app-note-tile',
  standalone: true,
  imports: [FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./home-widget.scss'],
  template: `
    <div class="widget-card" [style.--tile-accent]="tile.color">
      <div class="widget-head">
        <i class="ri-sticky-note-line" aria-hidden="true"></i>
        <span class="widget-title">{{ tile.name }}</span>
        @if (state === 'saving') { <span class="widget-meta">Speichert …</span> }
        @if (state === 'saved') { <span class="widget-meta">Gespeichert</span> }
      </div>
      <textarea
        class="note-text"
        [(ngModel)]="text"
        (ngModelChange)="scheduleSave()"
        (blur)="saveNow()"
        [readonly]="readonly"
        [attr.tabindex]="readonly ? -1 : null"
        [maxlength]="maxLength"
        placeholder="Hier etwas notieren …"
        [attr.aria-label]="'Notiz: ' + tile.name"
      ></textarea>
    </div>
  `,
})
export class NoteTileComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) public tile!: Tile;
  @Input() public readonly = false;
  public text = '';
  public state: 'idle' | 'saving' | 'saved' = 'idle';
  public readonly maxLength = MAX_NOTE_LENGTH;
  private savedText = '';
  private timer?: ReturnType<typeof setTimeout>;
  private saving: Promise<unknown> = Promise.resolve();
  private readonly tiles = inject(TileService);
  private readonly toastr = inject(ToastrService);
  private readonly cdr = inject(ChangeDetectorRef);

  public ngOnChanges(changes: SimpleChanges): void {
    // Neu geladene Kacheln überschreiben nicht, was gerade getippt wird.
    if (changes['tile'] && this.tile.widget?.type === 'note' && this.text === this.savedText) {
      this.text = this.savedText = this.tile.widget.text;
    }
  }

  public scheduleSave(): void {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.saveNow(), 800);
  }

  public saveNow(): void {
    clearTimeout(this.timer);
    this.timer = undefined;
    const text = this.text;
    if (text === this.savedText) return;
    this.savedText = text;
    this.state = 'saving';
    this.saving = this.saving.then(async () => {
      try {
        const res = await this.tiles.saveNote(this.tile._id, text);
        if (res.status !== 'ok') throw new Error(res.message);
        if (this.tile.widget?.type === 'note') this.tile.widget.text = text;
        this.state = this.text === text ? 'saved' : 'saving';
      } catch {
        // Beim nächsten Tippen oder Verlassen erneut versuchen.
        this.savedText = '\u0000';
        this.state = 'idle';
        this.toastr.error('Die Notiz konnte nicht gespeichert werden.');
      }
      this.cdr.markForCheck();
    });
  }

  public ngOnDestroy(): void {
    if (this.timer) this.saveNow();
  }
}
